import { useState, useCallback, useEffect, useRef } from 'react';
import type { Climb } from '../types/climb';

export type GroupBy = 'none' | 'setter' | 'grade' | 'zone';

export interface ClimbFilters {
  activeTab: 'boulders' | 'routes' | 'schedule' | 'board';
  boardId?: string; // Which board is selected when activeTab === 'board'
  gradeRange: [string, string] | null; // [minGrade, maxGrade] grade strings, null = all grades
  selectedRouteSetter: string;
  selectedHoldColor: string;
  /** Zone (sector) id, or 'all' — the toggle strip on the progress card. */
  selectedZone: string;
  selectedClimbTypes: string[]; // Array for multi-select
  selectedLocation?: string; // For user-profile context
  showUnofficial: boolean;
  showOnlyNotSent: boolean;
  sortBy: 'newest' | 'oldest' | 'easiest' | 'hardest';
  groupBy: GroupBy;
  olderThanDays?: string; // Number of days, staff-only filter
  date?: string; // YYYY-MM-DD, selected day on the schedule tab (deep-linkable)
}

const defaultFilters: ClimbFilters = {
  activeTab: 'boulders',
  gradeRange: null,
  selectedRouteSetter: 'all',
  selectedHoldColor: 'all',
  selectedZone: 'all',
  selectedClimbTypes: [],
  selectedLocation: 'all',
  showUnofficial: false,
  showOnlyNotSent: false,
  sortBy: 'newest',
  groupBy: 'none',
};

// Query param keys (short names for clean URLs)
const PARAM_MAP = {
  activeTab: 'tab',
  sortBy: 'sort',
  groupBy: 'group',
  selectedRouteSetter: 'setter',
  selectedHoldColor: 'color',
  selectedZone: 'zone',
  selectedLocation: 'location',
  boardId: 'board',
  showUnofficial: 'unofficial',
  showOnlyNotSent: 'not_sent',
  olderThanDays: 'older_than',
  gradeMin: 'grade_min',
  gradeMax: 'grade_max',
  date: 'date',
} as const;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function filtersFromParams(params: URLSearchParams): Partial<ClimbFilters> {
  const partial: Partial<ClimbFilters> = {};

  const tab = params.get(PARAM_MAP.activeTab);
  if (tab === 'boulders' || tab === 'routes' || tab === 'schedule' || tab === 'board') partial.activeTab = tab;

  const boardId = params.get(PARAM_MAP.boardId);
  if (boardId) partial.boardId = boardId;

  const sort = params.get(PARAM_MAP.sortBy);
  if (sort === 'newest' || sort === 'oldest' || sort === 'easiest' || sort === 'hardest') partial.sortBy = sort;

  const group = params.get(PARAM_MAP.groupBy);
  if (group === 'none' || group === 'setter' || group === 'grade' || group === 'zone') partial.groupBy = group;

  const setter = params.get(PARAM_MAP.selectedRouteSetter);
  if (setter) partial.selectedRouteSetter = setter;

  const color = params.get(PARAM_MAP.selectedHoldColor);
  if (color) partial.selectedHoldColor = color;

  const zone = params.get(PARAM_MAP.selectedZone);
  if (zone) partial.selectedZone = zone;

  const location = params.get(PARAM_MAP.selectedLocation);
  if (location) partial.selectedLocation = location;

  if (params.get(PARAM_MAP.showUnofficial) === '1') partial.showUnofficial = true;
  if (params.get(PARAM_MAP.showOnlyNotSent) === '1') partial.showOnlyNotSent = true;

  const olderThan = params.get(PARAM_MAP.olderThanDays);
  if (olderThan) partial.olderThanDays = olderThan;

  const date = params.get(PARAM_MAP.date);
  if (date && ISO_DATE_RE.test(date)) partial.date = date;

  const gradeMin = params.get(PARAM_MAP.gradeMin);
  const gradeMax = params.get(PARAM_MAP.gradeMax);
  if (gradeMin && gradeMax) partial.gradeRange = [gradeMin, gradeMax];

  return partial;
}

function filtersToParams(filters: ClimbFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.activeTab !== defaultFilters.activeTab) params.set(PARAM_MAP.activeTab, filters.activeTab);
  if (filters.sortBy !== defaultFilters.sortBy) params.set(PARAM_MAP.sortBy, filters.sortBy);
  if (filters.groupBy !== defaultFilters.groupBy) params.set(PARAM_MAP.groupBy, filters.groupBy);
  if (filters.selectedRouteSetter !== defaultFilters.selectedRouteSetter) params.set(PARAM_MAP.selectedRouteSetter, filters.selectedRouteSetter);
  if (filters.selectedHoldColor !== defaultFilters.selectedHoldColor) params.set(PARAM_MAP.selectedHoldColor, filters.selectedHoldColor);
  if (filters.selectedZone && filters.selectedZone !== defaultFilters.selectedZone) params.set(PARAM_MAP.selectedZone, filters.selectedZone);
  if (filters.selectedLocation && filters.selectedLocation !== defaultFilters.selectedLocation) params.set(PARAM_MAP.selectedLocation, filters.selectedLocation);
  if (filters.showUnofficial) params.set(PARAM_MAP.showUnofficial, '1');
  if (filters.showOnlyNotSent) params.set(PARAM_MAP.showOnlyNotSent, '1');
  if (filters.olderThanDays) params.set(PARAM_MAP.olderThanDays, filters.olderThanDays);
  // Only surface the day on the schedule tab, so it doesn't linger on other tabs.
  if (filters.date && filters.activeTab === 'schedule') params.set(PARAM_MAP.date, filters.date);
  // The selected board is only meaningful on a board tab.
  if (filters.boardId && filters.activeTab === 'board') params.set(PARAM_MAP.boardId, filters.boardId);
  if (filters.gradeRange) {
    params.set(PARAM_MAP.gradeMin, filters.gradeRange[0]);
    params.set(PARAM_MAP.gradeMax, filters.gradeRange[1]);
  }

  return params;
}

interface UseClimbFiltersOptions {
  searchParams?: URLSearchParams;
  setSearchParams?: (params: URLSearchParams, opts?: { replace?: boolean }) => void;
}

export function useClimbFilters(locationId: string | number, options?: UseClimbFiltersOptions) {
  const { searchParams, setSearchParams } = options || {};
  const isInitRef = useRef(false);

  const [filters, setFilters] = useState<ClimbFilters>(() => {
    // Priority: URL params > session storage > defaults
    let base = { ...defaultFilters };

    // Try session storage first
    const storageKey = `climbFilters_${locationId}`;
    const stored = sessionStorage.getItem(storageKey);
    if (stored) {
      try {
        base = { ...base, ...JSON.parse(stored) };
      } catch { /* ignore */ }
    }

    // URL params override everything
    if (searchParams && searchParams.toString()) {
      const fromUrl = filtersFromParams(searchParams);
      if (Object.keys(fromUrl).length > 0) {
        base = { ...base, ...fromUrl };
      }
    }

    return base;
  });

  // Sync filters → URL (skip the initial render to avoid replacing URL on mount when no params)
  useEffect(() => {
    if (!setSearchParams) return;

    if (!isInitRef.current) {
      isInitRef.current = true;
      // On init, only write to URL if there were already params or filters differ from default
      if (searchParams && searchParams.toString()) {
        const newParams = filtersToParams(filters);
        if (newParams.toString() !== searchParams.toString()) {
          setSearchParams(newParams, { replace: true });
        }
      }
      return;
    }

    const newParams = filtersToParams(filters);
    setSearchParams(newParams, { replace: true });
  }, [filters, setSearchParams, searchParams]);

  const updateFilters = useCallback((newFilters: Partial<ClimbFilters>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      const storageKey = `climbFilters_${locationId}`;
      sessionStorage.setItem(storageKey, JSON.stringify(updated));
      return updated;
    });
  }, [locationId]);

  return { filters, updateFilters };
}

export function useFilteredClimbsList(locationId?: string | number) {
  const saveFilteredClimbs = useCallback((climbs: Climb[], currentIndex: number = 0) => {
    const storageKey = locationId ? `filteredClimbs_${locationId}` : 'filteredClimbs';
    const indexKey = locationId ? `currentClimbIndex_${locationId}` : 'currentClimbIndex';
    const navigationKey = locationId ? `navigatingFromFilter_${locationId}` : 'navigatingFromFilter';

    sessionStorage.setItem(storageKey, JSON.stringify(climbs));
    sessionStorage.setItem(indexKey, String(currentIndex));
    // Set flag to indicate we're navigating from a filtered list
    sessionStorage.setItem(navigationKey, 'true');
  }, [locationId]);

  const getFilteredClimbs = useCallback((): { climbs: Climb[], currentIndex: number } => {
    try {
      const storageKey = locationId ? `filteredClimbs_${locationId}` : 'filteredClimbs';
      const indexKey = locationId ? `currentClimbIndex_${locationId}` : 'currentClimbIndex';

      const climbs = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
      const currentIndex = parseInt(sessionStorage.getItem(indexKey) || '0', 10);
      return { climbs, currentIndex };
    } catch {
      return { climbs: [], currentIndex: 0 };
    }
  }, [locationId]);

  const isNavigatingFromFilter = useCallback((): boolean => {
    const navigationKey = locationId ? `navigatingFromFilter_${locationId}` : 'navigatingFromFilter';
    return sessionStorage.getItem(navigationKey) === 'true';
  }, [locationId]);

  const clearNavigationFlag = useCallback(() => {
    const navigationKey = locationId ? `navigatingFromFilter_${locationId}` : 'navigatingFromFilter';
    sessionStorage.removeItem(navigationKey);
  }, [locationId]);

  const clearFilteredClimbs = useCallback(() => {
    const storageKey = locationId ? `filteredClimbs_${locationId}` : 'filteredClimbs';
    const indexKey = locationId ? `currentClimbIndex_${locationId}` : 'currentClimbIndex';
    const navigationKey = locationId ? `navigatingFromFilter_${locationId}` : 'navigatingFromFilter';

    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(indexKey);
    sessionStorage.removeItem(navigationKey);
  }, [locationId]);

  return {
    saveFilteredClimbs,
    getFilteredClimbs,
    clearFilteredClimbs,
    isNavigatingFromFilter,
    clearNavigationFlag
  };
}
